import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { DialogService } from '../../dialog/dialog.service';
import { FlagGroupService } from '../../flag/flag.group.service';
import { FlagService } from '../../flag/flag.service';
import { FlagGroupTagService } from '../../question/flag.group.tag.service';
import { SettingService } from '../../setting/setting.service';
import { Answer } from '../../shared/interfaces';
import { AnswerService } from '../answer.service';
import { InspectionService } from '../inspection.service';
import { setPictureDisplayTimestamp } from '../picture-display.utils';

@Component({
  selector: 'app-add-flag',
  templateUrl: './add.flag.component.html',
  styleUrls: ['./add.flag.component.scss'],
})
export class AddFlagComponent implements OnInit {
  @ViewChild('addFlagModal', { static: true }) public addFlagModal: ModalDirective;
  @ViewChild('ngSelectComponent', { static: true }) ngSelectComponent: any;

  confirmCallback: (text) => void;
  cancelCallback: () => void;
  answer: Answer;
  x: number;
  y: number;
  clientX: number;
  clientY: number;
  picture: any;

  modalStyle;

  addFlagForm: UntypedFormGroup;
  scoresEnabled = true;
  groups = [];
  showViewMore = false;
  searched = false;
  showOnlyAutoFlagTags = true;
  autoFlagTagsMapped = [];
  flagManagerMapped = [];
  autoFlagTagsAndFlagGroups = [];
  flagTypes = [];
  allFlagGroups = [];
  saving = false;

  get flagSubtypes() {
    const flagType = this.addFlagForm.get('flagType').value;
    if (flagType) {
      return flagType.subtypes;
    } else {
      return [];
    }
  }

  constructor(
    public answerService: AnswerService,
    public flagService: FlagService,
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    public settingService: SettingService,
    public inspectionService: InspectionService,
    private flagGroupService: FlagGroupService,
    private flagGroupTagService: FlagGroupTagService,
    private dialog: DialogService,
    private el: ElementRef,
  ) {}

  async ngOnInit() {
    this.addFlagModal.onHidden.subscribe(x => {
      if (x.dismissReason === 'esc' || x.dismissReason === 'backdrop-click') {
        this.cancel();
      }
    });

    this.addFlagForm = this.fb.group({
      flagType: '',
      flagSubtype: '',
      text: '',
      score: 0,
      group: [null, [this.groupNameValidator.bind(this)]],
    });

    const subtypeInfo = await this.flagService.subtypeInfo;
    this.flagTypes = (await this.flagService.listTypes()).filter(type => type.name !== 'Legacy');
    this.flagTypes.forEach(type => {
      type.subtypes.forEach(subtype => {
        subtype.name = this.flagService.getSubtypeInfo(subtypeInfo, subtype.name).name;
      });
    });

    this.addFlagForm.get('flagType').valueChanges.subscribe(type => {
      if (type) {
        this.addFlagForm.get('flagSubtype').setValue(type ? type.subtypes[0] : null);
      }
    });

    this.addFlagForm.get('flagType').setValue(this.flagTypes[1]);

    this.addFlagForm.get('flagSubtype').valueChanges.subscribe(async () => {
      await this.loadGroups();
      this.addFlagForm.get('group').updateValueAndValidity();
    });

    this.scoresEnabled = await this.settingService.scoresEnabled();
  }

  groupNameValidator(control: AbstractControl): { [key: string]: any } | null {
    if (this.addFlagForm) {
      const groupName = control.value || '';
      const groupExistWithOtherSubtype = this.allFlagGroups.find(
        g =>
          g.name.toUpperCase() === groupName.toUpperCase() &&
          g.subtypeId &&
          g.subtypeId !== this.addFlagForm.get('flagSubtype')?.value?.id,
      );
      if (groupExistWithOtherSubtype) {
        return { flagGroupExistsWithOtherSubtype: { value: control.value } };
      }
    }
    return null;
  }

  async addTagFn(name) {
    return { id: name, name: name, tag: true };
  }

  async loadGroups() {
    // Flag Groups
    const [allFlagManagerGroupTags] = await this.flagGroupService.listByInspection(this.answer.inspectionId);
    this.allFlagGroups = allFlagManagerGroupTags;
    this.flagManagerMapped = allFlagManagerGroupTags.map(fm => ({ id: fm.id, name: fm.name, type: 'flag' }));
    this.flagManagerMapped.sort((a, b) => a.name.localeCompare(b.name));

    // Auto Flag Tags
    this.autoFlagTagsMapped = this.answer.question.flagGroupTags.map(qt => ({
      id: qt.id,
      name: qt.name,
      type: 'question',
    }));
    this.autoFlagTagsMapped.sort((a, b) => a.name.localeCompare(b.name));

    this.evaluateGroups();
    // preselect first group
    if (this.groups && this.groups.length > 0 && this.groups[0].type !== 'viewMore') {
      this.addFlagForm.get('group').setValue(this.groups[0].name);
    }
  }

  async evaluateGroups() {
    // Check if we should show only Auto Flag Tags
    this.showOnlyAutoFlagTags = this.autoFlagTagsMapped.length > 0;

    // Combine both groups
    this.autoFlagTagsAndFlagGroups = [...this.autoFlagTagsMapped, ...this.flagManagerMapped];
    const uniqueGroups = this.filterUniqueGroups(this.autoFlagTagsAndFlagGroups);

    if (this.showOnlyAutoFlagTags) {
      // auto flag tags
      this.groups = uniqueGroups.filter(group => group.type === 'question');

      // Check for flag groups with type 'flag'
      const hasFlagTypeGroups = uniqueGroups.some(group => group.type === 'flag');
      if (hasFlagTypeGroups) {
        this.groups.push({ id: null, name: 'View more', type: 'viewMore', class: 'view-more-style' });
      }
    } else if (this.flagManagerMapped.length > 0) {
      this.groups = uniqueGroups.filter(group => group.type === 'flag');
    } else {
      this.groups = [];
    }
    const tags = await this.flagGroupTagService.list();
    this.groups = [...this.groups, ...tags.map(t => ({ id: null, name: t.name, type: 'flag' }))];
  }

  filterUniqueGroups(allGroups: any[]): any[] {
    const uniqueGroups = [];
    const addedGroupNames = new Set();

    allGroups.forEach(group => {
      if (!addedGroupNames.has(group.name) && this.groupNameValidator({ value: group.name } as any) === null) {
        uniqueGroups.push(group);
        addedGroupNames.add(group.name);
      }
    });
    return uniqueGroups;
  }

  onGroupSelected(selectedGroup: any) {
    if (selectedGroup?.type === 'viewMore') {
      this.onViewMoreClick();
      setTimeout(() => {
        this.ngSelectComponent.open();
      }, 0);
      this.addFlagForm.get('group').reset();
    }
  }

  onViewMoreClick() {
    this.showViewMore = true;
    const uniqueAutoFlagGroups = this.filterUniqueGroups(this.autoFlagTagsMapped);

    // Filter flag groups with the same name as autoflag groups (question config)
    const filteredFlagManagerMapped = this.flagManagerMapped.filter(flagGroup => {
      return !uniqueAutoFlagGroups.some(autoFlagGroup => autoFlagGroup.name === flagGroup.name);
    });
    this.groups = [...uniqueAutoFlagGroups, ...this.filterUniqueGroups(filteredFlagManagerMapped)];
  }

  onSearch(event: any) {
    if (event.term) {
      this.autoFlagTagsAndFlagGroups = [...this.autoFlagTagsMapped, ...this.flagManagerMapped];
      this.groups = this.filterUniqueGroups(this.autoFlagTagsAndFlagGroups);
      this.searched = true;
    } else {
      this.showViewMore = false;
      this.evaluateGroups();
    }
  }

  onClearSearch() {
    if (this.searched) {
      this.searched = false;
    }
    this.showViewMore = true;
    this.evaluateGroups();
  }

  async show(options: {
    confirmed?: (text) => void;
    canceled?: () => void;
    answer: Answer;
    picture?: any;
    coords?: any;
    initialType?: string;
  }) {
    this.confirmCallback = options.confirmed;
    this.cancelCallback = options.canceled;
    this.answer = options.answer;
    this.picture = options.picture;
    this.x = options.coords && options.coords.x;
    this.y = options.coords && options.coords.y;
    this.clientX = options.coords && options.coords.clientX;
    this.clientY = options.coords && options.coords.clientY;
    this.saving = false;

    await this.loadGroups();

    this.setPosition();
    this.addFlagModal.show();

    if (options.initialType) {
      this.addFlagForm.get('flagType').setValue(this.flagTypes.find(t => t.name === options.initialType));
    }

    this.addFlagForm.get('score').setValue(0);
  }

  setPosition() {
    if (typeof this.clientX !== 'number') {
      this.modalStyle = {};
      return;
    }

    const wrapperDiv = this.el.nativeElement.getElementsByClassName('modal')[0] as HTMLDivElement;
    const div = this.el.nativeElement.getElementsByClassName('modal-dialog')[0] as HTMLDivElement;

    wrapperDiv.style.display = 'block';
    const width = div.offsetWidth;
    const height = div.offsetHeight;
    wrapperDiv.style.display = 'none';
    const screenHeight = window.innerHeight;
    const screenWidth = window.innerWidth;

    let left = this.clientX + 30;
    const fitsX = screenWidth > width + this.clientX + 30;
    if (!fitsX) {
      left = this.clientX - width - 30;
    }

    const topAdjustment = Math.min(0, screenHeight - (height + this.clientY));

    this.modalStyle = {
      position: 'absolute',
      left: `${left}px`,
      top: `${this.clientY - 30 + topAdjustment}px`,
    };
  }

  async onSave() {
    if (this.saving) {
      return;
    }

    if (this.addFlagForm.valid) {
      this.saving = true;
      const formData = this.addFlagForm.value;
      const groupValue = this.addFlagForm.get('group').value;

      let flagGroupId = null;
      if (groupValue && groupValue !== '') {
        const group = this.groups.find(g => g.name === groupValue);
        if (group) {
          if (group.type === 'flag') {
            // Check if this flag group exists with same name and without subtype
            const groupExistWithoutSubtype = this.allFlagGroups.find(
              g => g.name === group.name && g.subtypeId === null,
            );
            // Check if this flag group exists with same name and subtype
            const groupExistWithSubtype = this.allFlagGroups.find(
              g => g.name === group.name && g.subtypeId === formData.flagSubtype.id,
            );

            if (groupExistWithSubtype) {
              flagGroupId = groupExistWithSubtype.id;
            } else if (groupExistWithoutSubtype) {
              flagGroupId = groupExistWithoutSubtype.id;
              // Change the subtype of the flag group
              groupExistWithoutSubtype.subtypeId = formData.flagSubtype.id;
              await this.flagGroupService.edit(flagGroupId, groupExistWithoutSubtype);
            } else {
              // If the group with subtype doesn't exist, create it
              const flagGroup = await this.flagGroupService.create(this.answer.inspectionId, {
                name: group.name,
                subtypeId: formData.flagSubtype.id,
              });
              flagGroupId = flagGroup.id;
            }
          } else if (group.type === 'question') {
            // Check if this flag group exists with same name and without subtype
            const groupExistWithSubtype = this.allFlagGroups.find(
              g => g.name === group.name && g.subtypeId === formData.flagSubtype.id,
            );
            // If the group with subtype doesn't exist, create it
            if (!groupExistWithSubtype) {
              const flagGroup = await this.flagGroupService.create(this.answer.inspectionId, {
                name: group.name,
                subtypeId: formData.flagSubtype.id,
              });
              flagGroupId = flagGroup.id;
            } else {
              flagGroupId = groupExistWithSubtype.id;
            }
          }
        } else {
          const flagGroup = await this.flagGroupService.create(this.answer.inspectionId, {
            name: groupValue,
            subtypeId: formData.flagSubtype.id,
          });
          flagGroupId = flagGroup.id;
        }
      }
      await this.save(formData, flagGroupId);
    }
  }

  async save(formData, flagGroupId) {
    const addedFlag = await this.flagService.create({
      answerId: this.answer.id,
      subtypeId: formData.flagSubtype.id,
      text: formData.text,
      picture: this.picture
        ? {
            ...this.picture,
            answer: null, // avoid circular reference
          }
        : null,
      y: this.y,
      x: this.x,
      score: formData.score,
      groupId: flagGroupId,
    });

    if (flagGroupId) {
      await this.flagService.addToGroup(flagGroupId, addedFlag.id);
    }

    const urlParams = (this.route.queryParams as any).value;

    // Possibly related flags
    const updatedAnswer = await this.answerService.get(this.answer.id);
    const answerChanges = await this.answerService.getChanges(this.answer.id);

    this.answer.flags.push(addedFlag);
    this.answer.flags.map(f => ({
      ...f,
      deeplink: [+urlParams.item, +urlParams.section, +urlParams.question],
    }));
    this.answer.changes = answerChanges;
    this.answer.flaggingScore = updatedAnswer.flaggingScore;
    this.answer.pictures.map(p => {
      const updatedPicture = updatedAnswer.pictures.find(up => up.id === p.id);
      if (updatedPicture) {
        p.updatedAt = updatedPicture.updatedAt;
        setPictureDisplayTimestamp(p, updatedPicture.updatedAt);
      }
    });
    if (this.picture) {
      const updatedPicture = updatedAnswer.pictures?.find(up => up.id === this.picture.id);
      if (updatedPicture) {
        this.picture.updatedAt = updatedPicture.updatedAt;
        setPictureDisplayTimestamp(this.picture, updatedPicture.updatedAt);
      }
    }
    this.addFlagModal.hide();
    this.addFlagForm.get('text').setValue('');
    this.addFlagForm.get('group').setValue(null);
    return this.confirmCallback && this.confirmCallback(formData.text);
  }

  cancel() {
    this.addFlagModal.hide();
    return !!this.cancelCallback && this.cancelCallback();
  }

  getValidationClass(field: string) {
    const control = this.addFlagForm.get(field);
    return {
      'is-valid': control.valid && (control.dirty || control.touched),
      'is-invalid': control.invalid && (control.dirty || control.touched),
    };
  }
}
